import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import api from '../services/api';
import { autoSyncPromoCodesOnLogin } from '../utils/promoSync';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  loading: boolean;
  login: (accessToken: string, refreshToken: string, userData: any) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const SimpleAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = () => {
      const isAuth = authService.isAuthenticated();
      const userData = authService.getUserData();
      
      setIsAuthenticated(isAuth);
      setUser(userData);
      setLoading(false);
      
      console.log('🔐 Auth check:', { isAuth, user: userData?.email });
    };

    checkAuth();

    // Écouter les événements de logout
    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [navigate]);

  const login = async (accessToken: string, refreshToken: string, userData: any) => {
    console.log('🔐 Login appelé:', { user: userData.email });
    authService.setTokens(accessToken, refreshToken, userData);
    setIsAuthenticated(true);
    setUser(userData);
    
    // Synchroniser les codes promo du localStorage vers le backend
    try {
      await autoSyncPromoCodesOnLogin();
    } catch (error) {
      console.error('⚠️ Erreur lors de la synchronisation des codes promo:', error);
    }
  };

  const logout = async () => {
    console.log('🚪 Logout appelé');
    
    // Appeler l'endpoint de logout pour marquer l'utilisateur comme hors ligne
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        console.log('👋 Envoi logout heartbeat...');
        await api.post('/api/users/logout/', {});
        console.log('✅ Logout heartbeat envoyé');
      }
    } catch (error) {
      console.error('⚠️ Erreur logout heartbeat (non bloquant):', error);
    }
    
    // Continuer avec le logout normal
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login', { replace: true });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
